import { useLang } from 'hooks/lang';
import { useState } from 'react';

import { actions } from 'entities';
import { useDirectNavigation } from 'routes';

import { Box, Column, Row } from 'components/Blocks';
import { GoBackButton } from 'components/Buttons';
import { CheckIcon } from 'components/Icons';

import { ProjectInfo } from './1.ProjectInfo';
import { Services } from './2.Services';
import { Costs } from './3.Costs';
import { Outsourced } from './4.Outsourced';
import { FinalDetails } from './5.FinalDetails';
import { FormWrapper } from './FormWrapper';

import { useFormControl } from 'hooks';

import { defaultCreationProps, StepComponentProps } from './types';

import { stepBlockers } from './utils';

import { cn } from 'styles';
import { getFirst } from 'utils/array';

const stepToComponent: Record<number, (props: StepComponentProps) => JSX.Element> = {
  1: ProjectInfo,
  2: Services,
  3: Costs,
  4: Outsourced,
  5: FinalDetails,
};

const steps = Array.from({ length: 5 }, (_, i) => i + 1);

function CreateProject() {
  const { state, dispatch, handleChange } = useFormControl(defaultCreationProps);

  const { t } = useLang();

  const createProject = actions.projects.createProject.use({
    params: {
      ...state,
      services: state.services.map(({ baseCosts: __, ...service }) => service),
      estimatedCompletionWorkingDays: Number(state.estimatedCompletionWorkingDays),
    },

    onSuccess: useDirectNavigation('home'),
  });

  const [step, setStep] = useState(1);

  const StepComponent = stepToComponent[step];

  const isStepBlocked = (wantedStep: number) =>
    steps
      .filter((singleStep) => singleStep <= wantedStep)
      .some((singleStep) => stepBlockers[singleStep]?.(state));

  const changeStep = (newStep: number) => {
    if (isStepBlocked(newStep)) return;

    setStep(newStep);
  };

  return (
    <Column gap="6">
      <Row>
        <GoBackButton />

        <h5 className="text-3xl font-medium text-primary">{t('create-project')}</h5>
      </Row>

      <Column className="w-full p-2" gap="6">
        <Row className="w-full" gap="0" relative>
          {steps.map((singleStep) => (
            <button
              type="button"
              key={`step-${singleStep}--`}
              onClick={() => changeStep(singleStep)}
              disabled={isStepBlocked(singleStep)}
              className={cn(
                'rounded-ful relative flex w-1/4 flex-col items-center gap-2',
                singleStep <= step && '',
              )}
            >
              {/* Right bar */}
              {singleStep < steps.length && (
                <div
                  className={`absolute left-1/2 top-1/2 h-1 w-1/2 -translate-y-1/2 ${singleStep < step ? 'bg-primary' : 'bg-muted'}`}
                />
              )}

              {/* Left bar */}
              {singleStep !== getFirst(steps) && (
                <div
                  className={`absolute right-1/2 top-1/2 h-1 w-1/2 -translate-y-1/2 ${singleStep <= step ? 'bg-primary' : 'bg-muted'}`}
                />
              )}

              <Box
                size={32}
                border="circle"
                className={cn(
                  'relative border text-sm font-bold',
                  singleStep === step && 'ring-2 ring-ring ring-offset-2',
                  singleStep <= step
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'bg-primary-foreground text-primary',
                )}
              >
                {singleStep < step ? <CheckIcon /> : singleStep}
              </Box>

              <p
                className={cn(
                  'absolute -bottom-8',
                  'text-sm font-medium transition-opacity phone:hidden',
                  step !== singleStep && 'opacity-60',
                )}
              >
                {t('step')}
              </p>
            </button>
          ))}
        </Row>

        <FormWrapper
          step={step}
          setStep={setStep}
          createProject={createProject.onClick}
          loading={createProject.loading}
          disabled={stepBlockers[step]?.(state)}
          className="mt-8 w-full rounded-lg border p-6 phone:mt-0"
        >
          <StepComponent
            state={state}
            dispatch={dispatch}
            handleChange={handleChange}
            setStep={setStep}
          />
        </FormWrapper>
      </Column>
    </Column>
  );
}

export default CreateProject;
